import React from 'react';
import { useTailwindBrand } from './index';

interface BrandButtonProps {
  variant?: 'primary' | 'secondary' | 'accent' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  children: React.ReactNode;
  onClick?: (() => void) | ((e: any) => void)
  disabled?: boolean;
  load?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  fullWidth?: boolean;
}

/**
 * Composant de bouton qui s'adapte automatiquement aux couleurs de la marque avec Tailwind
 */
export const BrandButton: React.FC<BrandButtonProps> = ({
  variant = 'primary',
  size = 'md',
  children,
  onClick,
  disabled = false,
  load,
  className = '',
  type = 'button',
  fullWidth = false
}) => {
  const { classes, combine } = useTailwindBrand();

  // Classes de taille
  const sizeClasses = {
    sm: 'px-2 py-2 /text-sm',
    md: 'px-4 py-2 /text-base',
    lg: 'px-6 py-2 /text-lg',
    xl: 'px-8 py-4 /text-xl'
  };

  // Classes de variante
  const variantClasses = {
    primary: `${classes.primary} ${classes.primaryHover} ${classes.fontFamily} lg:text-lg md:text-base sm:text-sm text-xs`,
    secondary: `${classes.secondary} ${classes.secondaryHover} ${classes.fontFamily} lg:text-lg md:text-base sm:text-sm text-xs`,
    accent: `${classes.accent} ${classes.accentHover}`,
    outline: `border-2 ${classes.primaryBorder} ${classes.primaryText} hover:${classes.primary} hover:text-white  ${classes.fontFamily} lg:text-lg md:text-base sm:text-sm text-xs`,
    ghost: `${classes.primaryText} hover:${classes.background} hover:${classes.primaryBorder} ${classes.fontFamily} lg:text-lg md:text-base sm:text-sm text-xs`
  };

  const baseClasses = `w-full py-2 px-6 rounded-lg font-bold text-lg text-white transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed lg:text-lg md:text-base text-sm drop-shadow-lg`;

  // Classes de largeur
  const widthClasses = fullWidth ? 'w-full' : '';

  const buttonClasses = combine(
    baseClasses,
    sizeClasses[size],
    variantClasses[variant],
    // focusClasses[variant],
    widthClasses,
    className
  );

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled}
    >
      {load ? (
        <div className="flex items-center justify-center gap-3">
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          <span>Chargement...</span>
        </div>
      ) : (
        children
      )}
    </button>
  );
};

/**
 * Bouton principal de la marque
 */
export const BrandPrimaryButton: React.FC<Omit<BrandButtonProps, 'variant'>> = (props) => (
  <BrandButton variant="primary" {...props} />
);

/**
 * Bouton secondaire de la marque
 */
export const BrandSecondaryButton: React.FC<Omit<BrandButtonProps, 'variant'>> = (props) => (
  <BrandButton variant="secondary" {...props} />
);

/**
 * Bouton d'accent de la marque
 */
export const BrandAccentButton: React.FC<Omit<BrandButtonProps, 'variant'>> = (props) => (
  <BrandButton variant="accent" {...props} />
);

/**
 * Bouton outline de la marque
 */
export const BrandOutlineButton: React.FC<Omit<BrandButtonProps, 'variant'>> = (props) => (
  <BrandButton variant="outline" {...props} />
);

/**
 * Bouton ghost de la marque
 */
export const BrandGhostButton: React.FC<Omit<BrandButtonProps, 'variant'>> = (props) => (
  <BrandButton variant="ghost" {...props} />
);

export default BrandButton;
